import { DataSource } from '@angular/cdk/table';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ComponentFactoryResolver,
  ContentChildren,
  Directive,
  EventEmitter,
  Input,
  Output,
  QueryList,
  TemplateRef,
  ViewChildren,
  ViewContainerRef,
} from '@angular/core';
import { PageEvent } from '@angular/material/paginator';
import { Sort } from '@angular/material/sort';
import { ActivatedRoute, NavigationExtras, Router } from '@angular/router';
import { Observable } from 'rxjs';
import { DataListImageCellComponent } from './image-cell/data-list-image-cell.component';

export interface DataListTableNavigationOptions {
  urlFragments?: string[];
  extrasBuilder?: (activatedRoute: ActivatedRoute) => NavigationExtras;
}

export interface ColumnConfig {
  key: string;
  title: string;
  sortable?: boolean;

  // Where isImage is true, render must evaluate to a file ID.
  isImage?: boolean;

  render?: (row: any) => string;
}

@Component({
  selector: 'data-list-simple-cell',
  template: ` {{ value }} `,
})
export class DataListSimpleCellComponent {
  @Input() value: string;
}

@Directive({
  selector: '[dataListCellRenderer]',
})
export class DataListCellRendererDirective {
  @Input() dataListCellRenderer: string;
  constructor(public template: TemplateRef<any>) {}
}

@Directive({
  selector: '[dataListCellPlaceholder]',
})
export class DataListCellPlaceholderDirective {
  @Input() dataListCellPlaceholder: string;
  @Input() payload: any;
  constructor(public viewContainer: ViewContainerRef) {}
}

@Component({
  selector: 'data-list-table',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './data-list-table.component.html',
  styleUrls: ['./data-list-table.component.scss'],
})
export class DataListTableComponent {
  // Whether or not to link rows. The string passed in will be the key to link to
  @Input() link: string;

  @Input() navigationOptions: DataListTableNavigationOptions;

  @Input() dividers = true;

  @Input() headings = true;

  @Input() paginator = true;

  // Observable data stream to be coerced into a dataSource
  @Input()
  set data(value: Observable<any>) {
    if (value) {
      this.dataSource = new SimpleDataSource(value);
    }
  }

  // DataSource to be passed to CDK
  @Input() dataSource: DataSource<any>;
  // Allows passing in a loaded pagination state
  @Input() page: PageEvent;
  // Table configuration
  @Input()
  set config(value: ColumnConfig[]) {
    this.columns = value || [];
    this.columnConfig.clear();
    this.columns.forEach((column) => this.columnConfig.set(column.key, column));
    this.displayedColumns = this.columns.map((value) => value.key);
  }

  // Allows passing in a loaded sort state
  @Input()
  set sort(value: string) {
    const [active, direction] = value ? value.split(' ') : [undefined, undefined];
    this.sortActive = active;
    this.sortDirection = direction ? direction.toLowerCase() : undefined;
  }

  // Emitted when page nubmer or items per page is changed
  @Output() onPage = new EventEmitter<PageEvent>();
  // Emitted when sort direction or column changed
  @Output() onSort = new EventEmitter<Sort>();

  // Cells rendered in the table.
  @ViewChildren(DataListCellPlaceholderDirective)
  set cell(values: QueryList<DataListCellPlaceholderDirective>) {
    this.cellPlaceholders.clear();
    if (values) {
      values.forEach((cell) => {
        const cells = this.cellPlaceholders.get(cell.dataListCellPlaceholder) || [];
        this.cellPlaceholders.set(cell.dataListCellPlaceholder, cells.concat(cell));
      });
    }
    this.rerender();
  }

  // Custom cell renderers from the parent
  @ContentChildren(DataListCellRendererDirective)
  set renderers(values: QueryList<DataListCellRendererDirective>) {
    this.cellRenderers.clear();
    if (values) {
      values.forEach((value) => {
        this.cellRenderers.set(value.dataListCellRenderer, value);
      });
    }
    this.rerender();
  }

  cellPlaceholders: Map<string, DataListCellPlaceholderDirective[]> = new Map();
  cellRenderers: Map<string, DataListCellRendererDirective> = new Map();
  columnConfig: Map<string, ColumnConfig> = new Map();
  columns: Array<ColumnConfig>;
  displayedColumns: Array<string>;
  sortActive: string;
  sortDirection: string;

  constructor(
    private changeDetector: ChangeDetectorRef,
    private componentFactoryResolver: ComponentFactoryResolver,
    private router: Router,
    private route: ActivatedRoute
  ) {}

  /**
   * Update all the cell placeholders passed to CDK with our custom renderers
   */
  rerender() {
    this.cellPlaceholders.forEach((placeholders, key) => {
      const renderer = this.cellRenderers.get(key);
      const config = this.columnConfig.get(key);
      placeholders.forEach((cell) => {
        cell.viewContainer.clear();
        if (renderer) {
          cell.viewContainer.createEmbeddedView(renderer.template, {
            $implicit: cell.payload,
          });
        } else {
          const payload = cell.payload || {};
          const rendered = config.render ? config.render(payload) : payload[key];
          if (config.isImage) {
            this.createImageCell(cell, rendered);
          } else {
            this.createSimpleCell(cell, rendered);
          }
        }
      });
    });
    this.changeDetector.detectChanges();
  }

  routeToItem(id: string) {
    this.router.navigate(
      [...(this.navigationOptions?.urlFragments ?? []), id],
      this.navigationOptions?.extrasBuilder(this.route) ?? { relativeTo: this.route }
    );
  }

  /**
   * Just display the column value (or some subset of the row) - no component necessary
   */
  createSimpleCell(target, payload: string) {
    const factory = this.componentFactoryResolver.resolveComponentFactory(
      DataListSimpleCellComponent
    );
    const cell = target.viewContainer.createComponent(factory);
    cell.instance.value = payload;
    return cell;
  }
  /**
   * Just display an image, based on the payload being an image file ID.
   */
  createImageCell(target, fileId: string) {
    const factory = this.componentFactoryResolver.resolveComponentFactory(
      DataListImageCellComponent
    );
    const cell = target.viewContainer.createComponent(factory);
    cell.instance.fileId = fileId;
    cell.instance.placeholder = target?.payload?.name ?? '';
    return cell;
  }
}

export class SimpleDataSource extends DataSource<any> {
  constructor(public data: Observable<any>) {
    super();
  }

  connect() {
    return this.data;
  }

  disconnect() {
    // lint
  }
}
